// Displays an individual T-account card with entries, totals, and drag/edit interactions.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface LineItemDragStartPayload {
  sequence: number;
  letter: string;
  displayReference: string;
  signedAmount: number;
  side: "debit" | "credit";
}

interface TAccountWidgetProps {
  account: string;
  accountNumber?: string;
  entries: Array<{
    debitAmount?: number;
    creditAmount?: number;
    letter: string;
    displayLetter: string;
    displayReference: string;
    sequence: number;
    comment?: string;
    isBeginningBalance?: boolean;
    isUnbalanced?: boolean;
  }>;
  highlightedLetter?: string | null;
  forceTwoDecimalDisplay?: boolean;
  formatNumbersWithCommas?: boolean;
  onAmountClick?: (reference: string, signedAmount: number) => void;
  onBeginningBalanceClick?: (accountName: string, signedAmount: number) => void;
  onLineItemDragStart?: (payload: LineItemDragStartPayload, event: React.MouseEvent<HTMLElement>) => void;
  onLineItemLabelHover?: (letter: string | null) => void;
  onAccountClick?: (accountName: string) => void;
  dragHandle?: React.ReactNode;
  isDragging?: boolean;
  activeDropColumn?: "debit" | "credit" | null;
}

export default function TAccountWidget({
  account,
  accountNumber,
  entries,
  highlightedLetter = null,
  forceTwoDecimalDisplay = false,
  formatNumbersWithCommas = false,
  onAmountClick,
  onBeginningBalanceClick,
  onLineItemDragStart,
  onLineItemLabelHover,
  onAccountClick,
  dragHandle,
  isDragging = false,
  activeDropColumn = null,
}: TAccountWidgetProps) {
  const accountTextRef = useRef<HTMLElement | null>(null);
  const [isAccountTextTruncated, setIsAccountTextTruncated] = useState(false);

  const updateAccountTextTruncation = useCallback(() => {
    const accountTextNode = accountTextRef.current;
    if (!accountTextNode) {
      setIsAccountTextTruncated(false);
      return;
    }

    const nextIsTruncated = (
      accountTextNode.scrollHeight - accountTextNode.clientHeight > 1
      || accountTextNode.scrollWidth - accountTextNode.clientWidth > 1
    );
    setIsAccountTextTruncated((previousValue) => (
      previousValue === nextIsTruncated ? previousValue : nextIsTruncated
    ));
  }, []);

  useEffect(() => {
    updateAccountTextTruncation();

    const accountTextNode = accountTextRef.current;
    if (!accountTextNode) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateAccountTextTruncation();
      });
      resizeObserver.observe(accountTextNode);
    }

    const handleWindowResize = () => {
      updateAccountTextTruncation();
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
      resizeObserver?.disconnect();
    };
  }, [account, updateAccountTextTruncation]);

  const formatAmount = (value?: number): string => {
    if (value === undefined) {
      return "";
    }

    if (formatNumbersWithCommas) {
      if (forceTwoDecimalDisplay) {
        return value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
      });
    }

    if (forceTwoDecimalDisplay) {
      return value.toFixed(2);
    }

    return String(value);
  };

  const debitTextValues = entries.map((entry) => {
    const amountText = formatAmount(entry.debitAmount);
    return amountText;
  });
  const creditTextValues = entries.map((entry) => {
    const amountText = formatAmount(entry.creditAmount);
    return amountText;
  });
  const hasEntries = entries.length > 0;
  const debitSubtotal = entries.reduce((total, entry) => total + (entry.debitAmount ?? 0), 0);
  const creditSubtotal = entries.reduce((total, entry) => total + (entry.creditAmount ?? 0), 0);
  const endingBalance = creditSubtotal - debitSubtotal;
  const endingBalanceAmount = Math.abs(endingBalance);
  const hasDebitEntries = entries.some((entry) => entry.debitAmount !== undefined);
  const hasCreditEntries = entries.some((entry) => entry.creditAmount !== undefined);
  const debitEntries = entries.filter((entry) => entry.debitAmount !== undefined);
  const creditEntries = entries.filter((entry) => entry.creditAmount !== undefined);
  const lineItemRowCount = Math.max(debitEntries.length, creditEntries.length, 2);
  const debitLineRows = Array.from({ length: lineItemRowCount }, (_, index) => debitEntries[index] ?? null);
  const creditLineRows = Array.from({ length: lineItemRowCount }, (_, index) => creditEntries[index] ?? null);
  const longestDebitLabelLength = entries.reduce((maxLength, entry) => (
    entry.debitAmount !== undefined
      ? Math.max(maxLength, entry.displayLetter.length)
      : maxLength
  ), 0);
  const longestCreditLabelLength = entries.reduce((maxLength, entry) => (
    entry.creditAmount !== undefined
      ? Math.max(maxLength, entry.displayLetter.length)
      : maxLength
  ), 0);
  const longestDebitLength = debitTextValues.reduce((maxLength, value) => Math.max(maxLength, value.length), 0);
  const longestCreditLength = creditTextValues.reduce((maxLength, value) => Math.max(maxLength, value.length), 0);
  const debitSubtotalText = hasDebitEntries ? formatAmount(debitSubtotal) : "";
  const creditSubtotalText = hasCreditEntries ? formatAmount(creditSubtotal) : "";
  const endingBalanceText = formatAmount(endingBalanceAmount);
  const debitSubtotalLength = debitSubtotalText.length;
  const creditSubtotalLength = creditSubtotalText.length;
  const endingBalanceLength = endingBalanceText.length;
  const sideContentLength = Math.max(
    longestDebitLength,
    longestCreditLength,
    debitSubtotalLength,
    creditSubtotalLength,
    endingBalanceLength,
    1,
  );
  const sideContentLengthWithMinimum = Math.max(sideContentLength, 4);
  const sideLabelLengthWithMinimum = Math.max(longestDebitLabelLength, longestCreditLabelLength, "EBal".length, 2);
  const sideLabelWidth = `calc(${sideLabelLengthWithMinimum}ch + 2px)`;
  const sideLabelGapPx = 10;
  const sideLabelGutter = `calc(${sideLabelWidth} + ${sideLabelGapPx}px)`;
  const columnWidth = `calc(${sideContentLengthWithMinimum}ch + 30px)`;
  const tWidth = `calc((${sideContentLengthWithMinimum}ch + 30px) * 2)`;
  const widgetWidth = `calc(${tWidth} + (${sideLabelGutter}) * 2)`;

  const renderLineItemContent = (
    entry: TAccountWidgetProps["entries"][number],
    amount: number,
    isDebit: boolean,
    isClickableOverride?: boolean,
    isItalicAmount?: boolean,
  ) => {
    const signedAmount = isDebit ? -Math.abs(amount) : Math.abs(amount);
    const isBeginningBalanceEntry = !!entry.isBeginningBalance;
    const isRelatedReferenceLabel = !isBeginningBalanceEntry && entry.displayReference !== "ebal";
    const isClickable = isClickableOverride ?? (isBeginningBalanceEntry ? !!onBeginningBalanceClick : !!onAmountClick);
    const isHighlighted = !isBeginningBalanceEntry && !!highlightedLetter && entry.letter === highlightedLetter;
    const referenceBaselineNudge = 2;

    const content = (
      <Box
        component={isClickable ? "button" : "div"}
        type={isClickable ? "button" : undefined}
        onMouseDown={isClickable ? (event) => event.preventDefault() : undefined}
        onClick={isClickable
          ? () => {
            if (isBeginningBalanceEntry) {
              onBeginningBalanceClick?.(account, signedAmount);
              return;
            }

            onAmountClick?.(entry.displayReference, signedAmount);
          }
          : undefined}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          backgroundColor: isHighlighted ? "primary.dark" : "transparent",
          borderRadius: 0.75,
          transition: "background-color 120ms ease-in-out",
          cursor: isClickable ? "pointer" : "default",
          color: "inherit",
          font: "inherit",
          lineHeight: "inherit",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "flex-end",
          width: "100%",
          position: "relative",
          overflow: "visible",
          px: 0.25,
          "&:hover": isClickable
            ? { backgroundColor: isHighlighted ? "primary.dark" : "rgba(0, 0, 0, 0.04)" }
            : undefined,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            bottom: `${referenceBaselineNudge}px`,
            ...(isDebit
              ? { right: `calc(100% + ${sideLabelGapPx}px)`, textAlign: "right" }
              : { left: `calc(100% + ${sideLabelGapPx}px)`, textAlign: "left" }),
            width: sideLabelWidth,
            fontStyle: "normal",
            fontSize: "0.74rem",
            color: entry.isUnbalanced ? "error.main" : "text.disabled",
            pointerEvents: isRelatedReferenceLabel ? "auto" : "none",
            cursor: isRelatedReferenceLabel ? "grab" : "default",
            "&:active": isRelatedReferenceLabel ? { cursor: "grabbing" } : undefined,
            lineHeight: 1,
          }}
          onMouseDown={isRelatedReferenceLabel
            ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onLineItemDragStart?.(
                {
                  sequence: entry.sequence,
                  letter: entry.letter,
                  displayReference: entry.displayReference,
                  signedAmount,
                  side: isDebit ? "debit" : "credit",
                },
                event,
              );
            }
            : undefined}
          onClick={isRelatedReferenceLabel ? (event) => event.stopPropagation() : undefined}
          onMouseEnter={isRelatedReferenceLabel ? () => onLineItemLabelHover?.(entry.letter) : undefined}
          onMouseLeave={isRelatedReferenceLabel ? () => onLineItemLabelHover?.(null) : undefined}
        >
          {entry.displayLetter}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontStyle: isItalicAmount || entry.isBeginningBalance ? "italic" : "normal",
            color: isHighlighted ? "common.white" : "text.primary",
          }}
        >
          {formatAmount(amount)}
        </Typography>
      </Box>
    );

    if (!isClickable) {
      return content;
    }

    return content;
  };

  const isAccountClickable = !!onAccountClick;
  const endingBalanceEntry = {
    sequence: Number.MAX_SAFE_INTEGER,
    letter: "ebal",
    displayLetter: "EBal",
    displayReference: "ebal",
  };
  const accountHeaderContent = (
    <Box
      component={isAccountClickable ? "button" : "div"}
      type={isAccountClickable ? "button" : undefined}
      onMouseDown={isAccountClickable ? (event) => event.preventDefault() : undefined}
      onClick={isAccountClickable ? () => onAccountClick(account) : undefined}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        width: "100%",
        backgroundColor: "transparent",
        borderRadius: 0.75,
        position: "relative",
        minWidth: 0,
        cursor: isAccountClickable ? "pointer" : "default",
        color: "inherit",
        font: "inherit",
        lineHeight: "inherit",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 0.75,
          backgroundColor: "transparent",
          transition: "background-color 120ms ease-in-out",
        },
        "&:hover::before": isAccountClickable ? { backgroundColor: "rgba(0, 0, 0, 0.04)" } : undefined,
      }}
    >
      <Typography
        ref={accountTextRef}
        variant="body2"
        fontWeight={600}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          lineHeight: 1.15,
          textAlign: "center",
          px: 0.5,
        }}
      >
        {account}
      </Typography>
    </Box>
  );
  const accountTooltipTitle = accountNumber ? `${account} / ${accountNumber}` : account;

  return (
    <Box
      sx={{
        width: widgetWidth,
        maxWidth: "100%",
        px: sideLabelGutter,
        position: "relative",
        backgroundColor: (theme) => alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.94 : 0.92,
        ),
        transformOrigin: "top right",
        transition: "transform 140ms ease, box-shadow 140ms ease",
        transform: isDragging ? "translateY(-4px) rotate(-2.2deg)" : "translateY(0) rotate(0deg)",
        boxShadow: isDragging ? "0 10px 24px rgba(0, 0, 0, 0.18)" : "none",
        animation: isDragging ? "t-account-lift 120ms ease-out" : "none",
        borderRadius: 1,
        "@keyframes t-account-lift": {
          from: {
            transform: "translateY(0) rotate(0deg)",
            boxShadow: "none",
          },
          to: {
            transform: "translateY(-4px) rotate(-2.2deg)",
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.18)",
          },
        },
        "& .t-account-drag-handle": {
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 120ms ease",
        },
        "&:hover .t-account-drag-handle": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      {dragHandle}
      <Box
        sx={{
          borderBottom: "2px solid",
          borderColor: "text.primary",
          px: 0.5,
          py: 0.25,
          minHeight: 44,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
        }}
      >
        <Tooltip
          title={accountTooltipTitle}
          placement="top"
          arrow
          describeChild
          disableHoverListener={!isAccountTextTruncated}
          disableFocusListener={!isAccountTextTruncated}
          disableTouchListener={!isAccountTextTruncated}
        >
          {accountHeaderContent}
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${columnWidth} ${columnWidth}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: activeDropColumn === "debit" ? "rgba(25, 118, 210, 0.12)" : "transparent",
            transition: "background-color 120ms ease",
          }}
        >
          {debitLineRows.map((entry, index) => (
            <Box
              key={entry ? `debit-${entry.sequence}` : `debit-empty-${index}`}
              sx={{
                px: 0.75,
                py: 0.25,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                minHeight: 24,
              }}
            >
              {entry ? renderLineItemContent(entry, entry.debitAmount ?? 0, true) : null}
            </Box>
          ))}

          {hasEntries ? (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "text.primary",
                px: 0.75,
                py: 0.25,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                textAlign: "right",
                cursor: "default",
                minHeight: 24,
              }}
            >
              {hasDebitEntries ? (
                <Typography variant="body2" fontWeight={600} sx={{ cursor: "default" }}>{debitSubtotalText}</Typography>
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: 24,
              }}
            />
          )}

          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "text.primary",
              px: 0.75,
              py: 0.25,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              minHeight: 24,
            }}
          >
            {endingBalance < 0 ? renderLineItemContent(endingBalanceEntry, endingBalanceAmount, true, false, true) : null}
          </Box>
        </Box>

        <Box
          sx={{
            borderLeft: "2px solid",
            borderColor: "text.primary",
            display: "flex",
            flexDirection: "column",
            backgroundColor: activeDropColumn === "credit" ? "rgba(25, 118, 210, 0.12)" : "transparent",
            transition: "background-color 120ms ease",
          }}
        >
          {creditLineRows.map((entry, index) => (
            <Box
              key={entry ? `credit-${entry.sequence}` : `credit-empty-${index}`}
              sx={{
                px: 0.75,
                py: 0.25,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                minHeight: 24,
              }}
            >
              {entry ? renderLineItemContent(entry, entry.creditAmount ?? 0, false) : null}
            </Box>
          ))}

          {hasEntries ? (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "text.primary",
                px: 0.75,
                py: 0.25,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                textAlign: "right",
                cursor: "default",
                minHeight: 24,
              }}
            >
              {hasCreditEntries ? (
                <Typography variant="body2" fontWeight={600} sx={{ cursor: "default" }}>{creditSubtotalText}</Typography>
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: 24,
              }}
            />
          )}

          <Box
            sx={{
              borderTop: "1px solid",
              borderTopColor: "text.primary",
              px: 0.75,
              py: 0.25,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              minHeight: 24,
            }}
          >
            {endingBalance >= 0 ? renderLineItemContent(endingBalanceEntry, endingBalanceAmount, false, false, true) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}